import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { RecapService } from '../services/recap.service';
import { finalize } from 'rxjs/operators';

@Component({
	selector: 'app-image-list',
	templateUrl: './image-list.component.html',
	styleUrls: ['./image-list.component.css']
})
export class ImageListComponent implements OnInit {

	@Input() game: RecapGameData;
	@Input() user: User;
	@Input() images: GameImage[] = [];

	@Output() newImages: EventEmitter<GameImage[]> = new EventEmitter();
	@Output() setBusy: EventEmitter<boolean> = new EventEmitter();

	focusImg: number = null;
	pendingBlock: boolean;
	blockReason: string = "";
	otherReason: string;
	blockPlayer: boolean;

	approved: GameImage[] = [];
	submitted: GameImage[] = [];

	err: string = "";
	loading: string = "";

	updatingApproved: boolean;

	rotation = 0;
	originalWidth = 0;
	originalHeight = 0;
	showConfirmationModal = false;
	savingRotatedImage = false;
	lastRotatedImgUrl = "";
	lastRotatedImgCode = "";

	constructor(private recapServe: RecapService) { }

	ngOnInit(): void {
		this.sortImages();
	}


	rotateImage(direction: string = "left"){
		if(direction == "left"){
			this.rotation = (this.rotation + 360 - 90) % 360;
		}
		if(direction == "right"){
			this.rotation = (this.rotation + 90) % 360;
		}
		this.confirmRotatedImage();
	}


	onImageLoad(event: Event) {
		const img = event.target as HTMLImageElement;
		this.originalWidth = img.naturalWidth;
		this.originalHeight = img.naturalHeight;
		this.rotation = 0;
	}

	getOrientation() {
		const rotated = this.rotation % 180 !== 0;
		const width = rotated ? this.originalHeight : this.originalWidth;
		const height = rotated ? this.originalWidth : this.originalHeight;
		return height >= width ? 'portrait' : 'landscape';
	}

	confirmRotatedImage(){
		if(this.rotation > 0){
			this.showConfirmationModal = true;
			this.saveRotatedImage();
		}
	}


	saveRotatedImage(){
		this.savingRotatedImage = true;
		let imgcode = this.submitted[this.focusImg].image_code;
		this.recapServe.replaceImage(this.game.gameid, this.rotation, imgcode)
		.subscribe((response)=>{
			this.lastRotatedImgUrl = response['url'];
			this.lastRotatedImgCode = imgcode;
			this.submitted[this.focusImg].object_name = response['object_name'];
			this.submitted[this.focusImg].orientation = response['orientation'];
			this.submitted[this.focusImg].url = response['url'];
			this.savingRotatedImage = false;
			this.showConfirmationModal = false;
		}, err =>{
			this.savingRotatedImage = false;
			this.showConfirmationModal = false;
			this.rotation = 0;
			setTimeout(() => {
				alert("An error occurred while saving the rotated image. Please try again.");
			}, 500);
			console.error("An error occured: ", err);
		});
	}

	updateSelectedRotatedImage(){
		this.submitted.forEach((item)=>{
			if( item.image_code == this.lastRotatedImgCode ){
				item.url = this.lastRotatedImgUrl;
			}
		});
	}

	closeFullScreen(){
		if(this.savingRotatedImage) return;
		this.updateSelectedRotatedImage();
		this.focusImg = null;
		this.rotation = 0;
		this.originalHeight = 0;
		this.originalWidth = 0;
	}

	sortImages() {
		this.approved = [];
		this.submitted = [];
		for (let im of this.images) {
			if (im.status === "submitted") {
				this.submitted.push(im)
			}
			else if (im.status === "approved") {
				this.approved.push(im)
			}
		}
		this.approved.sort((a: GameImage, b: GameImage) => {
			if (a.position === undefined) {
				return b.position >= 0 ? 1 : 0;
			}
			else if (b.position === undefined) {
				return b.position >= 0 ? -1 : 0;
			}
			if (a.position > b.position) return 1;
			else if (a.position < b.position) return -1;
			else return 0;
		})
		console.log(this.images);
	}

	unfocus() {
		this.focusImg = null;
		this.cancelBlock()
	}

	cancelBlock() {
		this.pendingBlock = false;
		this.blockReason = "";
		this.otherReason = "";
		this.blockPlayer = false;
	}

	approveImage(index: number) {
		if (this.submitted[index]) {
			if (this.approved.length < 9) {
				let sub = this.submitted[index];
				sub.pending_change = true;
				sub.status = "approved";
				sub.position = this.approved.length;
				this.approved.push(this.submitted.splice(index, 1)[0]);
				this.unfocus();
			}
		}
	}

	unapproveImage(index: number) {
		if (this.approved[index] && !this.updatingApproved) {
			this.updatingApproved = true;
			this.approved[index].pending_change = true;
			this.approved[index].status = "submitted";
			this.approved[index].position = null;
			this.submitted.push(this.approved.splice(index, 1)[0]);
			for (let i = 0; i < this.approved.length; i++) {
				let ap = this.approved[i];
				if (ap.position !== i) {
					ap.position = i;
					ap.pending_change = true;
				}
			}
			this.updatingApproved = false;
		}
	}

	blockImage(index: number) {
		this.err = "";
		this.loading = "Reporting..."
		if (!this.blockReason || (this.blockReason === 'other' && !this.otherReason)) {
			return;
		}
		else if (!this.submitted[index]) return;
		let blocked = this.submitted[index];
		blocked.status = "blocked";
		blocked.blocked_reason = this.blockReason;
		if (this.blockReason === 'other') {
			blocked.blocked_reason = `${this.blockReason}: ${this.otherReason}`
		}
		blocked.blocked_by = this.user.userid;
		this.recapServe.blockImage(
			this.game.gameid,
			blocked.image_code,
			blocked.blocked_reason,
			this.blockPlayer)
			.pipe(finalize(() => this.loading = ""))
			.subscribe(
				(res: GameImage[]) => {
					this.unfocus();
					this.sortImages();
				},
				err => this.err = err.error.detail)
	}

	prepFileUpload(e: any) {
		let files = e.target.files;
		this.setBusy.emit(true);
		this.loading = "Prepping Images..."
		this.err = "";
		let toUpload: File[] = [];
		let usedSlots = toUpload.length + this.approved.length;
		for (let i = 0; i < files.length; i++) {
			if (usedSlots >= 9) break;
			let f = files[i];
			if (f.type.match(/^image\//)) {
				if (f.size > 5242880) {
					this.err = `${f.name} is too large.`
					this.err = `${this.err} Please choose an image that 5MB or less.`
					this.loading = ""
					this.setBusy.emit(false);
					return;
				}
				toUpload.push(files[i]);
				usedSlots++;
			}
		}
		this.uploadImages(toUpload, toUpload.length, this.approved.length);
	}

	uploadImages(toUpload: File[], total: number = null, position: number = null) {
		if (total === null) total = toUpload.length;
		if (toUpload.length) {
			let current = total - toUpload.length + 1;
			this.loading = `Uploading image ${current}/${total}...`
			this.recapServe.uploadImage(this.game.gameid, toUpload.shift(), position)
				.subscribe(
					(res: GameImage[]) => {
						if (toUpload.length) {
							this.uploadImages(toUpload, total, position + 1);
						}
						else {
							let newImgs = [];
							for (let ri of res) {
								let found = false;
								for (let i of this.images) {
									if (i.image_code === ri.image_code) found = true;
								}
								if (!found) {
									this.images.push(ri);
									newImgs.push(ri);
								}
							}
							this.sortImages();
							this.newImages.emit(newImgs);
							this.setBusy.emit(false);
							this.loading = "";
							this.err = '';
						}
					},
					err => {
						this.err = err.error.detail;
						this.setBusy.emit(false);
						this.loading = '';
					})
		}
	}

}
